import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../components/Header";
import { AssetFinanceUrl, customerservice } from "../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";

const SEARCH_MAX_LENGTH = 13;
const INELIGIBLE_STATUSES = ["REPOSSESSED", "DISPOSED"];

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const parseErrorMessages = (rawError) => {
  try {
    const parsed = typeof rawError === "string" ? JSON.parse(rawError) : rawError;
    if (Array.isArray(parsed)) return parsed.map(String);
    if (typeof parsed === "string") return [parsed];
    if (parsed?.Error) {
      const messageText = parsed.Error.match(/MessageText:\s*(.*?)(?:\r?\n|$)/);
      if (messageText?.[1]) return [messageText[1].trim()];
      const systemException = parsed.Error.match(/System\.Exception: (.*?)\./);
      if (systemException?.[1]) return [systemException[1]];
      return [parsed.Error];
    }
    if (parsed?.error) return [String(parsed.error)];
    return [JSON.stringify(parsed)];
  } catch {
    return [String(rawError)];
  }
};

const showErrorMessages = (rawError, title = "Error!") => {
  const messages = parseErrorMessages(rawError);
  if (messages.length > 1) {
    return Swal.fire({
      icon: "error",
      title,
      html: `<ul style="text-align:left; margin:0; padding-left:1.2em;">${messages
        .map((message) => `<li>${escapeHtml(message)}</li>`)
        .join("")}</ul>`,
    });
  }
  return Swal.fire({
    icon: "error",
    title,
    text: messages[0] || "An unknown error occurred.",
  });
};

const validationSchema = yup.object().shape({
  narration: yup
    .string()
    .required("Please provide a reason for the voluntary repossession"),
});

const VoluntaryRepossessionForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);
  const actionBy = parseInt(decodedToken.Id);

  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);

  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState("");

  // Debounced customer search — the search box keeps whatever the maker is
  // typing (phone number, ID, or name, up to SEARCH_MAX_LENGTH characters).
  // A match is never written back into the box; it only surfaces below as a
  // confirmation card, so the maker can keep typing until they're satisfied.
  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setCustomer(null);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetchJsonDynamic(
          "dynamicCustomerSearch",
          "customer",
          { searchTerm: searchTerm.trim(), approval: 2, isAssetCustomer: 1 },
          customerservice.uri,
        );
        const found = response?.results ? JSON.parse(response.results) : null;
        if (found && found.Id) {
          setCustomer(found);
        } else {
          setCustomer(null);
        }
      } catch (error) {
        setCustomer(null);
      }
      setSearching(false);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    const fetchAssets = async () => {
      setSelectedAssetId("");
      if (!customer?.Id) {
        setAssets([]);
        return;
      }
      setLoadingAssets(true);
      try {
        const response = await fetchJsonDynamic(
          "collateralAssetByColumnValues",
          "asset",
          { customerId: Number(customer.Id) },
          AssetFinanceUrl.uri,
        );
        const allAssets = response?.results ? JSON.parse(response.results) : [];
        const eligible = (Array.isArray(allAssets) ? allAssets : []).filter(
          (asset) => !INELIGIBLE_STATUSES.includes(asset.AssetStatus),
        );
        setAssets(eligible);

        if (eligible.length === 1) {
          setSelectedAssetId(eligible[0].Id);
        }
      } catch (error) {
        setAssets([]);
      } finally {
        setLoadingAssets(false);
      }
    };
    fetchAssets();
  }, [customer]);

  const selectedAsset = assets.find((asset) => asset.Id === selectedAssetId) || null;

  const resetCustomer = () => {
    setCustomer(null);
    setSearchTerm("");
    setAssets([]);
    setSelectedAssetId("");
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!selectedAssetId) {
      Swal.fire(
        "Asset Required",
        "Please select the asset to voluntarily repossess.",
        "warning",
      );
      setSubmitting(false);
      return;
    }
    try {
      const voluntaryRepossession = {
        collateralAssetId: Number(selectedAssetId),
        actionBy,
        companyId,
        requestId: null,
        action: 0,
        narration: values.narration
          ? String(values.narration).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
          : null,
      };

      const response = await executeAnyMutation(
        "insertVoluntaryRepossession",
        voluntaryRepossession,
        "voluntaryRepossession",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.results) {
        props.onClose();
        props.onAction();
        resetForm();
        resetCustomer();
        await Swal.fire(
          "Success!",
          "Voluntary repossession request has been staged and is now pending approval.",
          "success",
        );
      } else if (response?.error) {
        await showErrorMessages(response.error);
      }
    } catch (error) {
      await Swal.fire(
        "Error!",
        "An error occurred while processing your request. Please try again later.",
        "error",
      );
    }
    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title="New Voluntary Repossession"
        subtitle="Search for a customer, select their asset, then request a voluntary repossession for a checker to approve"
      />

      <TextField
        fullWidth
        variant="filled"
        label="Search Customer (Name, Phone or ID number)"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={!!customer}
        inputProps={{ maxLength: SEARCH_MAX_LENGTH }}
        sx={{ mb: 2 }}
      />

      {searching && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Searching…</Typography>
        </Box>
      )}

      {!customer && !searching && searchTerm.trim().length >= 3 && (
        <Typography variant="body2" color="error" mb={2}>
          No approved customer found matching "{searchTerm}".
        </Typography>
      )}

      {customer && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {customer.FirstName} {customer.OtherNames}
                </Typography>
                <Typography variant="body2">
                  Phone: {customer.MobileNumber}
                </Typography>
                <Typography variant="body2">
                  ID/Passport: {customer.IdNoOrPassport || "N/A"}
                </Typography>
              </Box>
              <IconButton
                onClick={resetCustomer}
                size="small"
                title="Search a different customer"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {customer && loadingAssets && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Checking collateral assets…</Typography>
        </Box>
      )}

      {customer && !loadingAssets && assets.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No eligible collateral assets found for this customer.
        </Alert>
      )}

      {customer && !loadingAssets && assets.length > 0 && (
        <TextField
          select
          fullWidth
          variant="filled"
          label="Select Asset"
          value={selectedAssetId}
          onChange={(e) => setSelectedAssetId(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="" disabled>
            Select Asset
          </MenuItem>
          {assets.map((asset) => (
            <MenuItem key={asset.Id} value={asset.Id}>
              {asset.AssetTypeName}
              {asset.SubTypeName ? ` — ${asset.SubTypeName}` : ""} ·{" "}
              {String(asset.AssetStatus).replace(/_/g, " ")} ·{" "}
              {formatCurrency(asset.IndicativeValue)}
            </MenuItem>
          ))}
        </TextField>
      )}

      {selectedAsset && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Asset Details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))"
              gap={2}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Asset Type
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedAsset.AssetTypeName}
                  {selectedAsset.SubTypeName ? ` — ${selectedAsset.SubTypeName}` : ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Indicative Value
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(selectedAsset.IndicativeValue)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Current Status
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {String(selectedAsset.AssetStatus || "-").replace(/_/g, " ")}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {customer && selectedAssetId && (
        <Formik
          initialValues={{ narration: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              >
                <TextField
                  fullWidth
                  variant="filled"
                  multiline
                  minRows={2}
                  label="Reason for Voluntary Repossession *"
                  name="narration"
                  value={values.narration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.narration && !!errors.narration}
                  helperText={touched.narration && errors.narration}
                  sx={{ gridColumn: "span 4" }}
                />
              </Box>
              <Box display="flex" justifyContent="end" mt="20px">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Request Voluntary Repossession"}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default VoluntaryRepossessionForm;
